import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/** Returns true when Stripe Connect onboarding is still required for payouts. */
export function shouldShowStripeConnectButton(account) {
  return Boolean(account && account.payouts_enabled === false);
}

function getBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const encodedCredentials = btoa(`${username}:${password}`);
  return `Basic ${encodedCredentials}`;
}

/**
 * Shared Stripe Connect setup flow used on Billing (and dashboard snackbar side effects).
 * Preserves the same API calls and visibility rules as the former dashboard hero button.
 */
export function useStripeConnectSetup({ userId, onMissingAccount } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [showStripeButton, setShowStripeButton] = useState(false);
  const [account, setAccount] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const apiUrl1 = process.env.REACT_APP_API_URL1;

  const updateStripeAccountId = useCallback(async (instructorUserId, stripeAccountId) => {
    await axios.post(
      `${apiUrl}/api/users/instructor/updateStripeAccountId`,
      null,
      {
        params: {
          userId: instructorUserId,
          stripeAccountId,
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      }
    );
  }, [apiUrl]);

  const fetchStripeAccountIdFromStripe = useCallback(async () => {
    try {
      setIsLoading(true);

      const token = localStorage.getItem('token');
      const email = localStorage.getItem('email');
      const body = { email };

      const response = await axios.post(`${apiUrl1}/api/user/get-stripe_account_id`, body, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: token,
          'X-Api-Version': 'v2',
        },
      });

      const account = response.data.accountId;
      setAccount(account && typeof account === 'object' ? account : null);

      if (shouldShowStripeConnectButton(account)) {
        setShowStripeButton(true);
      } else if (account && account.payouts_enabled === true) {
        localStorage.setItem('inAppPurchaseEnable', 'true');
      }

      const storedStripeAccountId = localStorage.getItem('stripeAccountId');
      if ((storedStripeAccountId === null || storedStripeAccountId === 'null') && account?.id) {
        if (userId) {
          await updateStripeAccountId(userId, account.id);
        }
        localStorage.setItem('stripeAccountId', account.id);
      }
    } catch (error) {
      setAccount(null);
      if (error.response?.status === 404 && typeof onMissingAccount === 'function') {
        onMissingAccount(
          'To enable students to purchase courses through the app, please set up a Stripe account.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl1, onMissingAccount, updateStripeAccountId, userId]);

  const handleStripeOnboarding = useCallback(async () => {
    setIsLoading(true);
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('email');
    const body = { email };

    try {
      const response = await axios.post(`${apiUrl1}/api/user/create-account-and-onboarding-link`, body, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: token,
          'X-Api-Version': 'v2',
        },
      });
      window.location.href = response.data.accountLink.url;
    } catch (error) {
      // Preserve existing silent failure behavior from dashboard.
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl1]);

  useEffect(() => {
    fetchStripeAccountIdFromStripe();
  }, [fetchStripeAccountIdFromStripe]);

  return {
    showStripeButton,
    isLoading,
    handleStripeOnboarding,
    account,
  };
}
